import { jwtDecode } from "jwt-decode";
import { User } from "oidc-client";

import {
  AUTH_RESTORATION_RESULTS,
  bootstrapIdentityRestoration,
} from "./authRestorationService";
import { clearIncompleteAuthSession } from "./authCleanupService";
import { setAccountExpiredBlocked } from "./accountExpiredService";
import { getAuthProvider } from "./providers";
import { postMessageToApp } from "../../screens/utils/hybridAppHelpers";

const isTokenNotExpired = (expiresAtRaw) => {
  const expiresAt = Number(expiresAtRaw);
  if (!expiresAt) {
    return false;
  }

  // Support either epoch seconds or epoch milliseconds.
  const expiresAtMs =
    expiresAt > 1_000_000_000_000 ? expiresAt : expiresAt * 1000;
  return Date.now() < expiresAtMs;
};

export const hydrateUserManagerFromStorage = async ({
  userManager,
  onHydrationFailure,
}) => {
  // Always clear stale tokens before signaling failure.
  const handleHydrationFailure = async () => {
    await clearIncompleteAuthSession({ userManager });
    onHydrationFailure();
  };

  const user = await userManager.getUser();
  const userNotSetUp =
    !user || JSON.stringify(user) === "{}" || user.expired === true;

  const { access_token, id_token, refresh_token, expires_at } = localStorage;
  // In the mobile WebView the native shell owns the token lifecycle and keeps
  // the refresh token in SecureStore, so it is never injected here.
  const isMobileWebView = !!globalThis.ReactNativeWebView;
  const authInfoPresent = !!(
    access_token &&
    id_token &&
    (refresh_token || isMobileWebView) &&
    expires_at &&
    isTokenNotExpired(expires_at)
  );

  // Nothing to hydrate if user is already set up or tokens are missing/expired.
  if (!userNotSetUp || !authInfoPresent) {
    // In the WebView only native can renew. Tokens it handed over already
    // expired would otherwise send the SPA to sign-in, which native answers
    // by reinjecting the same stale tokens until it gives up and logs out.
    if (
      userNotSetUp &&
      isMobileWebView &&
      id_token &&
      expires_at &&
      !isTokenNotExpired(expires_at)
    ) {
      postMessageToApp("TokenRefreshNeeded", "");
    }
    return;
  }

  try {
    const idTokenClaims = jwtDecode(id_token);

    // Hydrate against whichever token the API authorizes on, matching
    // getApiBearerToken. When that's the ID token, the access token belongs to
    // someone else (Entra: Microsoft Graph): its lifetime is unrelated to the
    // session, and Microsoft does not guarantee it is even a readable JWT, so
    // decoding it here would fail the whole hydration for a token nothing
    // downstream uses.
    const provider = getAuthProvider();
    const idTokenIsBearer = provider.apiBearer === "id_token";
    const accessTokenClaims = idTokenIsBearer ? {} : jwtDecode(access_token);

    // Verify the bearer token's JWT exp before making the /userinfo network
    // call. Without this, every tab focus after natural token expiry fires a
    // doomed 401 request against the provider.
    const { exp } = idTokenIsBearer ? idTokenClaims : accessTokenClaims;
    if (exp && exp * 1000 < Date.now()) {
      await handleHydrationFailure();
      return;
    }

    // Strip OIDC metadata claims to get the profile data. Both providers put
    // the same profile data in the id_token as /userinfo, so no network call
    // is needed — which is also what makes this work in the WebView, where a
    // cross-origin /userinfo call is blocked.
    const {
      iss,
      aud,
      exp: _exp,
      iat,
      nonce,
      at_hash,
      c_hash,
      ...profile
    } = idTokenClaims;

    if (!profile || Object.keys(profile).length === 0) {
      await handleHydrationFailure();
      return;
    }

    // Merge remaining id_token claims into profile for full state.
    const {
      acr,
      auditTrackingId,
      auth_time,
      azp,
      s_hash,
      tokenName,
      tokenType,
    } = idTokenClaims;
    Object.assign(profile, {
      acr,
      auditTrackingId,
      auth_time,
      azp,
      s_hash,
      tokenName,
      tokenType,
    });

    // Build the OIDC User from stored tokens + decoded claims. When the ID token
    // is the bearer the access token was never decoded, so fall back to the
    // values every token response carries anyway.
    const { scope, token_type } = accessTokenClaims;
    const oidcUser = new User({
      access_token,
      expires_at: exp,
      id_token,
      profile,
      refresh_token,
      scope: scope ?? provider.scope,
      token_type: token_type ?? "Bearer",
    });
    await userManager.storeUser(oidcUser);

    // Attempt identity bootstrap (dealer data, profile info → localStorage).
    // In the mobile WebView, cap the wait so a slow or failing Engage API call
    // never blocks the auth machine indefinitely — 3 seconds is enough for
    // the happy path while keeping the perceived load time acceptable.
    const bootstrapResult = await runIdentityBootstrap({ profile });

    // The API refuses expired accounts; a stored session must not outlive that.
    if (bootstrapResult?.status === AUTH_RESTORATION_RESULTS.ACCOUNT_EXPIRED) {
      setAccountExpiredBlocked();
      await handleHydrationFailure();
    }
  } catch (error) {
    console.error("Error updating user manager from new access token.", error);
    await handleHydrationFailure();
  }
};

// Run identity bootstrap with platform-specific timeout handling. Resolves to
// the bootstrap result, or undefined if the WebView stopped waiting for it.
const runIdentityBootstrap = async ({ profile }) => {
  if (globalThis.ReactNativeWebView) {
    const MOBILE_BOOTSTRAP_TIMEOUT_MS = 3000;
    try {
      return await Promise.race([
        bootstrapIdentityRestoration({ forgeRockUserData: profile }),
        new Promise((resolve) =>
          globalThis.setTimeout(resolve, MOBILE_BOOTSTRAP_TIMEOUT_MS),
        ),
      ]);
    } catch (err) {
      console.warn(
        "WebView identity bootstrap failed; app-data phase will use fallback.",
        err,
      );
      return undefined;
    }
  }
  try {
    const bootstrapResult = await bootstrapIdentityRestoration({
      forgeRockUserData: profile,
    });
    if (bootstrapResult.status !== AUTH_RESTORATION_RESULTS.READY) {
      console.warn(
        "Identity bootstrap did not complete during hydration; will retry on authenticated.",
      );
    }
    return bootstrapResult;
  } catch (bootstrapError) {
    console.warn(
      "Identity bootstrap threw during hydration; will retry on authenticated.",
      bootstrapError,
    );
    return undefined;
  }
};
